import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { CalendarClock, Eye, FileText, Loader2, TriangleAlert, Users } from "lucide-react";
import type {
  CampaignDto,
  CampaignRecipientListItem,
  CampaignReviewResponse,
  CampaignStatusName,
  WhatsAppConnectionResponse,
} from "@multi-chat/shared";
import { getCampaignReview } from "../../api/campaigns-api.js";
import { getConnection } from "../../api/whatsapp-api.js";
import { useWhatsAppTemplateHeaderImageUrl } from "../whatsapp/use-whatsapp-template-header-image.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import {
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import {
  buildClientTemplatePresentation,
  discoverTemplateSendSlots,
} from "../inbox/template-send.js";
import { ChannelIcon } from "../../components/channel/ChannelIcon.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { campaignFailureMessage, mapCampaignError } from "./campaigns-errors.js";
import {
  buildPreviewValuesMap,
  contactFieldLabelKey,
  describeMappingEntry,
  senderDisplayName,
  usableWhatsAppSenders,
  variableTokenLabel,
} from "./campaign-message-presentation.js";
import {
  campaignReadonlyPreviewTiming,
  formatCampaignScheduleInstantParts,
} from "./campaign-review-presentation.js";
import { CampaignLivePreview, CampaignPreviewRecipientPicker } from "./CampaignLivePreview.js";
import { CampaignModalContent } from "./CampaignModalContent.js";
import { CampaignPhonePreview } from "./CampaignPhonePreview.js";
import { CampaignStatusBadge } from "./CampaignStatusBadge.js";

const valueClass = "m-0 max-w-full text-[0.875rem] font-semibold leading-snug text-foreground";
const secondaryClass = "m-0 max-w-full text-[0.75rem] leading-snug text-muted";

/** Desktop: the device must fit the xl modal body (modal height minus header, footer and picker). */
const previewPhoneFrameClass =
  "max-w-[clamp(15rem,calc((100dvh-12rem)*0.45),18.75rem)] lg:max-w-[clamp(13rem,calc((min(92dvh,52rem)-15rem)*0.45),18.75rem)]";

function previewSubtitle(t: TFunction, status: CampaignStatusName): string {
  switch (status) {
    case "SCHEDULED":
      return t("campaigns.results.previewSubtitleUpcoming");
    case "RUNNING":
    case "COMPLETED":
    case "FAILED":
    case "CANCELLED":
    case "DRAFT":
      return t("campaigns.results.previewSubtitleReadOnly");
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function PreviewSummaryRow({
  icon,
  iconToneClass,
  label,
  testId,
  children,
}: {
  icon: ReactNode;
  iconToneClass: string;
  label: string;
  testId: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3 py-3" data-testid={testId}>
      <span
        className={cn("grid size-8 shrink-0 place-items-center rounded-lg", iconToneClass)}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="grid min-w-0 flex-1 gap-0.5">
        <dt className="text-[0.75rem] font-medium text-muted">{label}</dt>
        <dd className="m-0 grid min-w-0 justify-items-start gap-0.5">{children}</dd>
      </div>
    </div>
  );
}

export function CampaignPreviewDialog({
  open,
  organizationId,
  campaign,
  recipients,
  onOpenChange,
}: {
  open: boolean;
  organizationId: number;
  campaign: CampaignDto;
  recipients: readonly CampaignRecipientListItem[];
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [review, setReview] = useState<CampaignReviewResponse | null>(null);
  const [connection, setConnection] = useState<WhatsAppConnectionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [previewRecipientId, setPreviewRecipientId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [nextReview, conn] = await Promise.all([
          getCampaignReview(organizationId, campaign.id, controller.signal),
          getConnection(organizationId, controller.signal).catch(() => null),
        ]);
        if (controller.signal.aborted) return;
        setReview(nextReview);
        setConnection(conn);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setError(caught);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [open, organizationId, campaign.id, campaign.updatedAt]);

  useEffect(() => {
    if (recipients.length === 0) {
      setPreviewRecipientId(null);
      return;
    }
    setPreviewRecipientId((current) => {
      if (current != null && recipients.some((item) => item.id === current)) {
        return current;
      }
      return recipients[0]!.id;
    });
  }, [recipients]);

  const sampleRecipient =
    previewRecipientId != null
      ? (recipients.find((item) => item.id === previewRecipientId) ?? null)
      : null;
  const senders = useMemo(() => usableWhatsAppSenders(connection), [connection]);
  const selectedSender =
    campaign.platformEndpointId != null
      ? (senders.find((item) => item.platformEndpointId === campaign.platformEndpointId) ?? null)
      : null;
  const discovery = useMemo(
    () =>
      campaign.templateSnapshot.components != null
        ? discoverTemplateSendSlots(campaign.templateSnapshot.components)
        : null,
    [campaign.templateSnapshot.components],
  );
  const headerImageUrl = useWhatsAppTemplateHeaderImageUrl(
    organizationId,
    campaign.whatsappTemplateId,
    discovery?.kind === "supported" && discovery.headerFormat === "IMAGE",
  );
  const previewPresentation = useMemo(() => {
    if (!discovery || discovery.kind !== "supported") return null;
    const { valuesBySlotKey, missingParameterKeys } = buildPreviewValuesMap(
      campaign.variableMapping,
      sampleRecipient?.personalizationSnapshot ?? null,
      discovery.slots,
    );
    return {
      missingParameterKeys,
      presentation: buildClientTemplatePresentation({
        templateName: campaign.templateSnapshot.name ?? t("campaigns.message.template"),
        headerText: discovery.headerText,
        headerFormat: discovery.headerFormat,
        headerImageUrl,
        headerImageMissing: discovery.headerFormat === "IMAGE" && !headerImageUrl,
        bodyText: discovery.bodyText,
        footerText: discovery.footerText,
        buttons: discovery.buttons,
        slots: discovery.slots,
        valuesBySlotKey,
      }),
      language: campaign.templateSnapshot.language ?? null,
    };
  }, [campaign.templateSnapshot, campaign.variableMapping, discovery, headerImageUrl, sampleRecipient, t]);

  const missingLabels = useMemo(() => {
    return (previewPresentation?.missingParameterKeys ?? []).map((key) => {
      const entry = campaign.variableMapping.variables[key];
      if (!entry) return variableTokenLabel(key);
      const described = describeMappingEntry(entry);
      if (described.source === "CONTACT_FIELD") {
        return t(contactFieldLabelKey(described.detail as never));
      }
      return described.detail;
    });
  }, [campaign.variableMapping.variables, previewPresentation?.missingParameterKeys, t]);

  const recipientCount =
    campaign.audienceSnapshotSummary?.readyRecipients ?? review?.readyRecipientCount ?? null;
  const timezone = review?.organizationTimezone ?? null;
  const timingKind = campaignReadonlyPreviewTiming({
    status: campaign.status,
    scheduledAt: campaign.scheduledAt,
    startedAt: campaign.startedAt,
    completedAt: campaign.completedAt,
  });
  let timing: { label: string; iso: string; showTimezone: boolean } | null = null;
  switch (timingKind) {
    case "schedule":
      timing = campaign.scheduledAt
        ? {
            label: t("campaigns.results.previewTimingScheduled"),
            iso: campaign.scheduledAt,
            showTimezone: true,
          }
        : null;
      break;
    case "started":
      timing = campaign.startedAt
        ? {
            label: t("campaigns.results.previewTimingStarted"),
            iso: campaign.startedAt,
            showTimezone: false,
          }
        : null;
      break;
    case "completed":
      timing = campaign.completedAt
        ? {
            label: t("campaigns.results.previewTimingCompleted"),
            iso: campaign.completedAt,
            showTimezone: false,
          }
        : null;
      break;
    case "status":
      break;
    default: {
      const _exhaustive: never = timingKind;
      return _exhaustive;
    }
  }
  const timingParts =
    timing && timezone
      ? formatCampaignScheduleInstantParts(timing.iso, timezone, i18n.language)
      : null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent size="xl" data-testid="campaign-preview-dialog">
        <div className={cn(contactsModalHeaderClass, "border-b border-line/60 pb-4")}>
          <div className="flex items-start gap-3">
            <span
              className="inline-grid size-9 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-ink"
              aria-hidden="true"
            >
              <Eye size={16} />
            </span>
            <div className="grid min-w-0 flex-1 gap-0.5">
              <AlertDialogTitle>{t("campaigns.results.previewTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{previewSubtitle(t, campaign.status)}</AlertDialogDescription>
            </div>
          </div>
        </div>

        <div
          className="grid min-h-0 flex-1 overflow-y-auto overscroll-contain lg:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden"
          data-testid="campaign-preview-layout"
        >
          <section
            className="grid min-w-0 content-start gap-3 border-b border-line/60 px-6 py-5 lg:border-b-0 lg:border-e"
            data-testid="campaign-preview-summary"
          >
            <div className="grid gap-1.5" data-testid="campaign-preview-status">
              <p className="m-0 text-[0.75rem] font-medium text-muted">
                {t("campaigns.list.columns.status")}
              </p>
              <div>
                <CampaignStatusBadge status={campaign.status} />
              </div>
            </div>

            {error ? (
              <p className="m-0 text-[0.75rem] text-error" role="alert">
                {mapCampaignError(t, error, t("campaigns.errors.requestFailed"))}
              </p>
            ) : null}

            <dl className="m-0 grid divide-y divide-line/60 border-t border-line/60">
              <PreviewSummaryRow
                icon={<Users size={15} />}
                iconToneClass="bg-accent/10 text-accent"
                label={t("campaigns.review.checklist.audience")}
                testId="campaign-preview-audience"
              >
                <p className={cn(valueClass, "tabular-nums")}>
                  {recipientCount != null
                    ? new Intl.NumberFormat(i18n.language).format(recipientCount)
                    : "—"}
                </p>
              </PreviewSummaryRow>

              {selectedSender ? (
                <PreviewSummaryRow
                  icon={<ChannelIcon platformType="WHATSAPP" size={15} />}
                  iconToneClass="bg-whatsapp-muted text-whatsapp"
                  label={t("campaigns.review.checklist.sender")}
                  testId="campaign-preview-sender"
                >
                  <bdi className={cn(valueClass, "break-words")}>
                    {senderDisplayName(selectedSender)}
                  </bdi>
                  {selectedSender.phoneNumber ? (
                    <LtrValue className={cn(secondaryClass, "tabular-nums")}>
                      {selectedSender.phoneNumber}
                    </LtrValue>
                  ) : null}
                </PreviewSummaryRow>
              ) : null}

              <PreviewSummaryRow
                icon={<FileText size={15} />}
                iconToneClass="bg-primary/[0.07] text-primary/80"
                label={t("campaigns.review.checklist.template")}
                testId="campaign-preview-template"
              >
                <bdi className={cn(valueClass, "break-all")}>
                  {campaign.templateSnapshot.name ?? "—"}
                </bdi>
              </PreviewSummaryRow>

              {timing ? (
                <PreviewSummaryRow
                  icon={<CalendarClock size={15} />}
                  iconToneClass="bg-surface-muted text-ink"
                  label={timing.label}
                  testId="campaign-preview-timing"
                >
                  {timingParts ? (
                    <>
                      <p className={valueClass}>
                        {timingParts.date}
                        <span className="font-normal text-muted" aria-hidden="true">
                          {" · "}
                        </span>
                        <LtrValue className="tabular-nums">{timingParts.time}</LtrValue>
                      </p>
                      {timing.showTimezone && timezone ? (
                        <LtrValue className={secondaryClass}>{timezone}</LtrValue>
                      ) : null}
                    </>
                  ) : loading ? (
                    <Loader2 size={14} className="animate-spin text-muted" aria-label={t("campaigns.loading")} />
                  ) : (
                    <p className={valueClass}>—</p>
                  )}
                </PreviewSummaryRow>
              ) : null}
            </dl>

            {campaign.status === "FAILED" ? (
              <div
                className="grid gap-1 rounded-lg border border-error/30 bg-error-muted px-3 py-2 text-error"
                data-testid="campaign-preview-failure"
                role="status"
              >
                <p className="m-0 flex items-center gap-1.5 text-[0.8125rem] font-semibold">
                  <TriangleAlert size={14} aria-hidden="true" />
                  {t("campaigns.results.campaignFailed")}
                </p>
                <p className="m-0 text-[0.8125rem]">
                  {campaignFailureMessage(t, campaign.failureCode)}
                </p>
              </div>
            ) : null}
          </section>

          <section
            className="grid min-w-0 content-start justify-items-center gap-3 bg-surface-muted/50 px-4 py-4 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:[align-content:safe_center] lg:[scrollbar-width:thin] lg:[scrollbar-color:var(--color-line-strong)_transparent]"
            data-testid="campaign-preview-phone-area"
          >
            <CampaignPreviewRecipientPicker
              recipients={recipients}
              previewRecipientId={previewRecipientId}
              onPreviewRecipientChange={setPreviewRecipientId}
              className="w-full max-w-[22rem] justify-center"
              testId="campaign-preview-recipient-picker"
            />
            <div className="w-full max-w-[22rem]">
              <CampaignLivePreview
                recipients={recipients}
                previewRecipientId={previewRecipientId}
                onPreviewRecipientChange={setPreviewRecipientId}
                presentation={previewPresentation?.presentation ?? null}
                language={previewPresentation?.language ?? null}
                missingLabels={missingLabels}
                sectionTestId="campaign-preview-live"
                workspace
                renderFrame={(preview) => (
                  <CampaignPhonePreview
                    senderName={
                      selectedSender
                        ? senderDisplayName(selectedSender)
                        : t("campaigns.results.previewPhoneSenderFallback")
                    }
                    senderPhone={selectedSender?.phoneNumber ?? null}
                    contentKey={`${campaign.id}:${previewRecipientId ?? "none"}`}
                    frameClassName={previewPhoneFrameClass}
                  >
                    {preview}
                  </CampaignPhonePreview>
                )}
              />
            </div>
          </section>
        </div>

        <AlertDialogActions className={contactsModalFooterClass}>
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </AlertDialogActions>
      </CampaignModalContent>
    </AlertDialog>
  );
}
